import React, { useState, useEffect, useRef, useCallback } from 'react'
import { IMultiSelect } from '@api'
import GenericSelectWithInfiniteScroll, {
  ItemProps,
  ListFuc,
} from './GenericSelectWithInfiniteScroll'
import css from './multiSelect.module.scss'
import MultiSelect from './MultiSelect'
import IconButton from '@ui/buttons/IconButton'

interface MultiSelectProps<T> {
  values: T[]
  onChange: (items: T[], param?: any) => void
  onClear?: () => void
  placeHolder?: string
  onSelectDeselect?: () => void
  title: string
  disabled?: boolean
  listData?: T[]
  width?: number | string
  icon?: React.ReactNode
  numOfRows?: number
  itemRenderer: React.FC<ItemProps<T>>
  canCreate?: boolean
  multi?: boolean
  onCreate?: (item: string) => void
  customClass?: { multiCustomClass: string }
  valueRenderer?: (param: T) => React.ReactNode
  list: ListFuc<T>
}

export default function MultiSelectComponent<T extends IMultiSelect>({
  values,
  onChange,
  onClear,
  placeHolder,
  list,
  icon,
  itemRenderer,
  canCreate,
  onCreate,
  multi,
  customClass,
  valueRenderer = (value) => (
    <span key={value.id} className="m-2">
      {value.name}
    </span>
  ),
  disabled,
}: MultiSelectProps<T>) {
  const [data, setData] = useState(values)
  const handleClear = () => {
    setData([])
    onClear && onClear()
  }
  const employeeRef = useRef<HTMLDivElement>(null)
  const [top, setTop] = useState<number>(0)

  const getTop = useCallback(() => {
    const divHeight = employeeRef?.current?.children[0]?.clientHeight
    return divHeight ? (divHeight > 100 ? 24 : 0) : 0
  }, [])

  const renderer = (
    <div style={{ display: 'flex' }}>
      <div className={`p-2  ${customClass?.multiCustomClass ?? css.multi_select_generic}`}>
        <div style={{ position: 'sticky', top: top }} className="pr-1">
          {icon}
        </div>
        <div ref={employeeRef} className={css.selectedEmployess}>
          {data.length ? (
            <MultiSelect
              values={values.map((value) => valueRenderer(value))}
              select={null}
              onRemove={(id) => {
                !disabled &&
                  onChange(
                    values.filter((emp) => emp.id !== id),
                    { id, status: 'remove' }
                  )
              }}
            />
          ) : (
            placeHolder || 'Search...'
          )}
        </div>
        {onClear && (
          <IconButton
            style={{
              visibility: values.length ? 'visible' : 'hidden',
              position: 'sticky',
              top: top,
            }}
            round={false}
            minimal
            iconName="close"
            onClick={handleClear}
          />
        )}
      </div>
    </div>
  )
  useEffect(() => {
    setData(values)
    setTop(getTop())
  }, [values, getTop])

  const itemDisabled = (item: T) => data.some((e) => e.id === item.id)
  const selectDeselectHandler = (node: T | null) => {
    const valueExist = data.some(({ id }) => id === node?.id)
    const status = { status: valueExist ? 'remove' : 'add', id: node?.id }
    const value = valueExist
      ? data.filter(({ id }) => id !== node?.id)
      : node
      ? multi
        ? [...data, node]
        : [node]
      : data
    setData(value)
    onChange(value, status)
  }
  return (
    <GenericSelectWithInfiniteScroll<T>
      renderer={renderer}
      list={list}
      multi={multi}
      Item={itemRenderer}
      value={null}
      itemDisabled={itemDisabled}
      onChange={selectDeselectHandler}
      canCreate={canCreate}
      onCreate={onCreate}
    />
  )
}
